import React, { Component } from 'react';
import AuthService from './AuthService';

const Auth = new AuthService();

class AnnouncementForm extends Component {
  constructor() {
    super();
    this.state = {
      announcements: [],
      editing: null, // null = list view, 'new' = create form, or announcement ID for edit
      viewingHistory: null, // null or announcement object to view history
      formData: {
        title: '',
        message: '',
        image: '',
        imageOption: 'fallback',
        buttons: [],
        platform: 'app'
      },
      env: 'Dev',
      loading: false,
      error: null
    };
    this.handleEnv = this.handleEnv.bind(this);
    this.fetchAnnouncements = this.fetchAnnouncements.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.handleActivate = this.handleActivate.bind(this);
    this.handleDeactivate = this.handleDeactivate.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.resetForm = this.resetForm.bind(this);
    this.handleViewHistory = this.handleViewHistory.bind(this);
    this.handleAddButton = this.handleAddButton.bind(this);
    this.handleRemoveButton = this.handleRemoveButton.bind(this);
    this.handleButtonChange = this.handleButtonChange.bind(this);
  }

  componentDidMount() {
    this.fetchAnnouncements();
  }

  getBaseUrl() {
    if (this.state.env === 'Local') {
      return 'http://localhost:8090';
    } else if (this.state.env === 'Dev') {
      return 'https://dev.primaltcg.com';
    } else {
      return 'https://api.primaltcg.com';
    }
  }

  getFetch() {
    if (this.state.env === 'Local') {
      return Auth.localFetch.bind(Auth);
    } else if (this.state.env === 'Dev') {
      return Auth.devFetch.bind(Auth);
    } else {
      return Auth.prodFetch.bind(Auth);
    }
  }

  handleEnv(changeEvent) {
    this.setState({
      env: changeEvent.target.value,
      editing: null
    }, () => {
      this.fetchAnnouncements();
    });
  }

  async fetchAnnouncements() {
    this.setState({ loading: true, error: null });
    try {
      const baseUrl = this.getBaseUrl();
      const fetch = this.getFetch();
      const announcements = await fetch(`${baseUrl}/api/v1.1/announcements/history`, {
        method: 'GET'
      });
      this.setState({ announcements, loading: false });
    } catch (e) {
      this.setState({ error: 'Failed to fetch announcements', loading: false });
      console.error(e);
    }
  }

  handleInputChange(e) {
    const { name, value, type, checked } = e.target;
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  }

  resetForm() {
    this.setState({
      editing: null,
      formData: {
        title: '',
        message: '',
        image: '',
        imageOption: 'fallback',
        buttons: [],
        platform: 'app'
      },
      error: null
    });
  }

  handleEdit(announcement) {
    this.setState({
      editing: announcement._id,
      formData: {
        title: announcement.title,
        message: announcement.message,
        image: announcement.image || '',
        imageOption: announcement.imageOption || 'fallback',
        buttons: announcement.buttons || [],
        platform: announcement.platform || 'app'
      }
    });
  }

  async handleSubmit(e) {
    e.preventDefault();
    const { formData, editing } = this.state;
    const baseUrl = this.getBaseUrl();
    const fetch = this.getFetch();

    // Validate required fields
    if (!formData.title || !formData.message) {
      this.setState({ error: 'Title and message are required' });
      return;
    }

    // Validate custom image
    if (formData.imageOption === 'custom' && !formData.image) {
      this.setState({ error: 'Please provide an image URL or select a different image option' });
      return;
    }

    // Validate buttons - remove empty ones or show error
    const validButtons = formData.buttons.filter(b => b.title && b.link);
    const invalidButtons = formData.buttons.filter(b => (b.title && !b.link) || (!b.title && b.link));

    if (invalidButtons.length > 0) {
      this.setState({ error: 'Each button must have both a title and a link. Remove empty buttons or fill in the missing fields.' });
      return;
    }

    // Use only valid buttons (removes completely empty ones)
    const dataToSave = { ...formData, buttons: validButtons };

    this.setState({ loading: true, error: null });

    try {
      if (editing === 'new') {
        // Create new announcement
        await fetch(`${baseUrl}/api/v1.1/announcements`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSave)
        });
        alert(`Announcement created on ${this.state.env}!`);
      } else {
        // Update existing announcement
        await fetch(`${baseUrl}/api/v1.1/announcements/${editing}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSave)
        });
        alert(`Announcement updated on ${this.state.env}!`);
      }
      this.resetForm();
      this.fetchAnnouncements();
    } catch (e) {
      this.setState({ error: 'Failed to save announcement', loading: false });
      console.error(e);
    }
  }

  async handleActivate(id) {
    if (!window.confirm('Are you sure you want to activate this announcement? This will deactivate other announcements for the same platform.')) {
      return;
    }

    const baseUrl = this.getBaseUrl();
    const fetch = this.getFetch();

    this.setState({ loading: true, error: null });

    try {
      await fetch(`${baseUrl}/api/v1.1/announcements/${id}/activate`, {
        method: 'PATCH'
      });
      alert(`Announcement activated on ${this.state.env}!`);
      this.fetchAnnouncements();
    } catch (e) {
      this.setState({ error: 'Failed to activate announcement', loading: false });
      console.error(e);
    }
  }

  async handleDeactivate(id) {
    if (!window.confirm('Are you sure you want to deactivate this announcement?')) {
      return;
    }

    const baseUrl = this.getBaseUrl();
    const fetch = this.getFetch();

    this.setState({ loading: true, error: null });

    try {
      await fetch(`${baseUrl}/api/v1.1/announcements/${id}/deactivate`, {
        method: 'PATCH'
      });
      alert(`Announcement deactivated on ${this.state.env}!`);
      this.fetchAnnouncements();
    } catch (e) {
      this.setState({ error: 'Failed to deactivate announcement', loading: false });
      console.error(e);
    }
  }

  async handleDelete(id) {
    if (!window.confirm('Are you sure you want to delete this announcement? This action cannot be undone.')) {
      return;
    }

    const baseUrl = this.getBaseUrl();
    const fetch = this.getFetch();

    this.setState({ loading: true, error: null });

    try {
      await fetch(`${baseUrl}/api/v1.1/announcements/${id}`, {
        method: 'DELETE'
      });
      alert(`Announcement deleted from ${this.state.env}!`);
      this.fetchAnnouncements();
    } catch (e) {
      this.setState({ error: 'Failed to delete announcement', loading: false });
      console.error(e);
    }
  }

  formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  handleViewHistory(announcement) {
    this.setState({ viewingHistory: announcement });
  }

  handleAddButton() {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        buttons: [
          ...prevState.formData.buttons,
          { title: '', link: '', backgroundColor: '#ffffff', textColor: '#000000' }
        ]
      }
    }));
  }

  handleRemoveButton(index) {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        buttons: prevState.formData.buttons.filter((_, i) => i !== index)
      }
    }));
  }

  handleButtonChange(index, field, value) {
    this.setState(prevState => {
      const buttons = [...prevState.formData.buttons];
      buttons[index] = { ...buttons[index], [field]: value };
      return {
        formData: {
          ...prevState.formData,
          buttons
        }
      };
    });
  }

  renderHistory() {
    const { viewingHistory } = this.state;
    const history = viewingHistory.history || [];

    return (
      <div className="announcement-history">
        <div className="history-header">
          <h3>History: {viewingHistory.title}</h3>
          <button
            className="btn btn-secondary"
            onClick={() => this.setState({ viewingHistory: null })}
          >
            Back to List
          </button>
        </div>

        {history.length === 0 ? (
          <p className="no-history">No history available for this announcement.</p>
        ) : (
          <div className="history-timeline">
            {history.map((entry, index) => (
              <div key={index} className="history-entry">
                <div className="history-action">
                  <span className={`action-badge action-${entry.action}`}>
                    {entry.action.toUpperCase()}
                  </span>
                  <span className="history-date">{this.formatDate(entry.timestamp)}</span>
                </div>
                {entry.changes && Object.keys(entry.changes).length > 0 && (
                  <div className="history-changes">
                    {Object.entries(entry.changes).map(([field, change]) => (
                      <div key={field} className="change-item">
                        <strong>{field}:</strong>{' '}
                        {field === 'buttons' ? (
                          <div className="buttons-change-detail">
                            {(() => {
                              const fromButtons = Array.isArray(change.from) ? change.from : [];
                              const toButtons = Array.isArray(change.to) ? change.to : [];

                              return (
                                <>
                                  {fromButtons.length === 0 && toButtons.length > 0 && (
                                    <div className="change-to">
                                      Added {toButtons.length} button(s):
                                      {toButtons.map((b, i) => (
                                        <div key={i} className="button-detail">
                                          "{b.title}" → {b.link}
                                          <span className="color-preview" style={{backgroundColor: b.backgroundColor, color: b.textColor, padding: '2px 6px', borderRadius: '3px', marginLeft: '5px', fontSize: '10px'}}>color</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {fromButtons.length > 0 && toButtons.length === 0 && (
                                    <div className="change-from">
                                      Removed {fromButtons.length} button(s):
                                      {fromButtons.map((b, i) => (
                                        <div key={i} className="button-detail">"{b.title}"</div>
                                      ))}
                                    </div>
                                  )}
                                  {fromButtons.length > 0 && toButtons.length > 0 && (
                                    <>
                                      <div className="change-from">
                                        Before:
                                        {fromButtons.map((b, i) => (
                                          <div key={i} className="button-detail">
                                            "{b.title}" → {b.link}
                                            <span className="color-preview" style={{backgroundColor: b.backgroundColor, color: b.textColor, padding: '2px 6px', borderRadius: '3px', marginLeft: '5px', fontSize: '10px'}}>color</span>
                                          </div>
                                        ))}
                                      </div>
                                      <div className="change-to">
                                        After:
                                        {toButtons.map((b, i) => (
                                          <div key={i} className="button-detail">
                                            "{b.title}" → {b.link}
                                            <span className="color-preview" style={{backgroundColor: b.backgroundColor, color: b.textColor, padding: '2px 6px', borderRadius: '3px', marginLeft: '5px', fontSize: '10px'}}>color</span>
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        ) : (
                          <>
                            <span className="change-from">{String(change.from || '(empty)')}</span>
                            {' → '}
                            <span className="change-to">{String(change.to || '(empty)')}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  renderForm() {
    const { formData, editing, loading } = this.state;
    const isNew = editing === 'new';

    return (
      <div className="announcement-form">
        <h3>{isNew ? 'Create New Announcement' : 'Edit Announcement'}</h3>
        <form onSubmit={this.handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <span className="help-text">The main headline shown on the banner</span>
            <input
              type="text"
              name="title"
              className="form-control"
              value={formData.title}
              onChange={this.handleInputChange}
              placeholder="e.g., Set 4 Pre-Orders Available!"
              required
            />
          </div>

          <div className="form-group">
            <label>Message *</label>
            <span className="help-text">Description text shown below the title</span>
            <textarea
              name="message"
              className="form-control"
              rows="5"
              value={formData.message}
              onChange={this.handleInputChange}
              placeholder="e.g., Pre-order now and get exclusive promos!"
              required
            />
          </div>

          <div className="form-group">
            <label>Image Option</label>
            <span className="help-text">Choose what image appears at the top of the banner</span>
            <div className="image-option-selector">
              <label className="image-option">
                <input
                  type="radio"
                  name="imageOption"
                  value="fallback"
                  checked={formData.imageOption === 'fallback'}
                  onChange={this.handleInputChange}
                />
                {' '}Use Default (Set 4 Logo)
              </label>
              <label className="image-option">
                <input
                  type="radio"
                  name="imageOption"
                  value="custom"
                  checked={formData.imageOption === 'custom'}
                  onChange={this.handleInputChange}
                />
                {' '}Custom Image
              </label>
              <label className="image-option">
                <input
                  type="radio"
                  name="imageOption"
                  value="none"
                  checked={formData.imageOption === 'none'}
                  onChange={this.handleInputChange}
                />
                {' '}No Image
              </label>
            </div>
          </div>

          {formData.imageOption === 'custom' && (
            <>
              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="text"
                  name="image"
                  className="form-control"
                  value={formData.image}
                  onChange={this.handleInputChange}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              {formData.image && (
                <div className="form-group">
                  <label>Image Preview</label>
                  <div className="image-preview">
                    <img src={formData.image} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px' }} />
                    <button
                      type="button"
                      className="btn btn-sm btn-remove-image"
                      onClick={() => this.setState(prevState => ({
                        formData: { ...prevState.formData, image: '' }
                      }))}
                    >
                      Remove Image
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="form-group">
            <label>Buttons</label>
            <span className="help-text">Add clickable buttons to the banner. Leave empty for no buttons.</span>
            <div className="buttons-editor">
              {formData.buttons.map((button, index) => (
                <div key={index} className="button-item">
                  <div className="button-row">
                    <input
                      type="text"
                      className="form-control"
                      value={button.title}
                      onChange={(e) => this.handleButtonChange(index, 'title', e.target.value)}
                      placeholder="e.g., Pre-Order Now"
                    />
                    <input
                      type="text"
                      className="form-control"
                      value={button.link}
                      onChange={(e) => this.handleButtonChange(index, 'link', e.target.value)}
                      placeholder="e.g., /new-genesis or https://shop.primaltcg.com"
                    />
                  </div>
                  <div className="button-row">
                    <label className="color-label">
                      Background:
                      <input
                        type="color"
                        value={button.backgroundColor}
                        onChange={(e) => this.handleButtonChange(index, 'backgroundColor', e.target.value)}
                      />
                    </label>
                    <label className="color-label">
                      Text:
                      <input
                        type="color"
                        value={button.textColor}
                        onChange={(e) => this.handleButtonChange(index, 'textColor', e.target.value)}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn btn-sm btn-remove"
                      onClick={() => this.handleRemoveButton(index)}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="button-preview">
                    <span
                      style={{
                        backgroundColor: button.backgroundColor,
                        color: button.textColor,
                        padding: '8px 16px',
                        borderRadius: '20px',
                        display: 'inline-block'
                      }}
                    >
                      {button.title || 'Button Preview'}
                    </span>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-add-button"
                onClick={this.handleAddButton}
              >
                + Add Button
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Platform *</label>
            <span className="help-text">Where should this announcement appear? App and Website can have different active announcements.</span>
            <div className="platform-selector">
              <label className="platform-option">
                <input
                  type="radio"
                  name="platform"
                  value="app"
                  checked={formData.platform === 'app'}
                  onChange={this.handleInputChange}
                />
                {' '}App Only
              </label>
              <label className="platform-option">
                <input
                  type="radio"
                  name="platform"
                  value="website"
                  checked={formData.platform === 'website'}
                  onChange={this.handleInputChange}
                />
                {' '}Website Only
              </label>
              <label className="platform-option">
                <input
                  type="radio"
                  name="platform"
                  value="both"
                  checked={formData.platform === 'both'}
                  onChange={this.handleInputChange}
                />
                {' '}Both
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Announcement'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={this.resetForm}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  renderList() {
    const { announcements, loading } = this.state;

    if (loading) {
      return <div className="loading">Loading announcements...</div>;
    }

    return (
      <div className="announcement-list">
        <div className="list-header">
          <h3>Announcements</h3>
          <button
            className="btn btn-create"
            onClick={() => this.setState({ editing: 'new' })}
          >
            Create New
          </button>
        </div>

        {announcements.length === 0 ? (
          <p className="no-announcements">No announcements found. Create one to get started.</p>
        ) : (
          <table className="announcement-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Platform</th>
                <th>Title</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map(announcement => (
                <tr key={announcement._id} className={announcement.active ? 'active-row' : ''}>
                  <td>
                    <span className={`status-badge ${announcement.active ? 'active' : 'inactive'}`}>
                      {announcement.active ? 'ACTIVE' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span className={`platform-badge platform-${announcement.platform || 'app'}`}>
                      {(announcement.platform || 'app').toUpperCase()}
                    </span>
                  </td>
                  <td>{announcement.title}</td>
                  <td>{this.formatDate(announcement.createdAt)}</td>
                  <td className="actions">
                    <button
                      className="btn btn-sm btn-edit"
                      onClick={() => this.handleEdit(announcement)}
                    >
                      Edit
                    </button>
                    {!announcement.active ? (
                      <button
                        className="btn btn-sm btn-activate"
                        onClick={() => this.handleActivate(announcement._id)}
                      >
                        Activate
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-deactivate"
                        onClick={() => this.handleDeactivate(announcement._id)}
                      >
                        Deactivate
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-history"
                      onClick={() => this.handleViewHistory(announcement)}
                    >
                      History
                    </button>
                    <button
                      className="btn btn-sm btn-delete"
                      onClick={() => this.handleDelete(announcement._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  render() {
    const { editing, error, env, viewingHistory } = this.state;

    const envColors = {
      Local: { bg: '#E3F2FD', border: '#1976D2', text: '#1565C0' },
      Dev: { bg: '#FFF3E0', border: '#F57C00', text: '#E65100' },
      Live: { bg: '#FFEBEE', border: '#D32F2F', text: '#C62828' }
    };

    return (
      <div className="announcement-form-container">
        <div
          className="env-toggle"
          style={{
            backgroundColor: envColors[env].bg,
            borderLeft: `4px solid ${envColors[env].border}`,
            padding: '15px',
            marginBottom: '20px',
            borderRadius: '4px'
          }}
        >
          <h4 style={{ color: envColors[env].text, marginTop: 0 }}>
            Environment: <strong>{env}</strong>
            {env === 'Live' && <span style={{ marginLeft: '10px' }}>⚠️ Production</span>}
          </h4>
          <div className="env-selector" style={{ display: 'flex', gap: '20px' }}>
            <label className={`env-option ${env === 'Local' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="env"
                value="Local"
                checked={env === 'Local'}
                onChange={this.handleEnv}
              />
              {' '}Local
            </label>
            <label className={`env-option ${env === 'Dev' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="env"
                value="Dev"
                checked={env === 'Dev'}
                onChange={this.handleEnv}
              />
              {' '}Dev
            </label>
            <label className={`env-option ${env === 'Live' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="env"
                value="Live"
                checked={env === 'Live'}
                onChange={this.handleEnv}
              />
              {' '}Live
            </label>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {viewingHistory !== null
          ? this.renderHistory()
          : editing !== null
            ? this.renderForm()
            : this.renderList()}
      </div>
    );
  }
}

export default AnnouncementForm;
